import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";
import { UploadCloud, X, Loader2, Star, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, rectSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** Resolve `/api/files/{id}` URL to full absolute URL. */
export function resolveImageUrl(u) {
  if (!u) return null;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

/** Upload a single file to /api/admin/uploads. */
async function uploadFile(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/admin/uploads", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
}

function Thumb({ url, id, index, total, onRemove, testid }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : "auto",
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group rounded-xl overflow-hidden bg-ivory-100 border border-ink-200/40 aspect-square touch-none ${isDragging ? "shadow-elevated" : ""}`}
      data-testid={`${testid}-item-${index}`}
    >
      <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover pointer-events-none" loading="lazy" />
      {/* Drag handle - top-left */}
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-white/90 text-ink-700 flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition"
        aria-label="Trascina"
        data-testid={`${testid}-drag-${index}`}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {/* Remove */}
      <button
        type="button"
        onClick={() => onRemove(index)}
        data-testid={`${testid}-remove-${index}`}
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
        aria-label="Rimuovi"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {index === 0 && (
        <span className="absolute bottom-1.5 left-1.5 text-[9px] font-semibold uppercase tracking-wider bg-white/95 text-bronze-500 px-2 py-0.5 rounded-full flex items-center gap-1 shadow-soft">
          <Star className="w-2.5 h-2.5" /> Copertina
        </span>
      )}
    </div>
  );
}

/**
 * Reusable uploader.
 *  - single: value is a URL string (or null)
 *  - multiple: value is an array of URL strings — supports drag & drop reorder
 */
export default function ImageUploader({
  value,
  onChange,
  onBusyChange,
  multiple = false,
  label,
  hint,
  className = "",
  testid = "image-uploader",
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);

  const list = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const setBusyBoth = (b) => {
    setBusy(b);
    if (typeof onBusyChange === "function") onBusyChange(b);
  };

  const handleFiles = useCallback(
    async (files) => {
      const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
      if (!arr.length) return;
      setBusyBoth(true);
      try {
        for (const f of arr) {
          try {
            const res = await uploadFile(f);
            const url = res.urls.medium;
            if (multiple) {
              const base = Array.isArray(valueRef.current) ? valueRef.current : [];
              const next = [...base, url];
              valueRef.current = next;
              onChange(next);
            } else {
              valueRef.current = url;
              onChange(url);
            }
          } catch (e) {
            toast.error(apiErrorMessage(e) || "Upload fallito");
          }
        }
      } finally {
        setBusyBoth(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multiple, onChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    if (multiple) {
      const next = [...(Array.isArray(value) ? value : [])];
      next.splice(idx, 1);
      onChange(next);
    } else {
      onChange(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = list.findIndex((u, i) => `${u}-${i}` === active.id);
    const newIndex = list.findIndex((u, i) => `${u}-${i}` === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(list, oldIndex, newIndex));
  };

  const items = list.map((u, i) => `${u}-${i}`);

  return (
    <div className={className}>
      {label && (
        <label className="text-[11px] uppercase tracking-overline font-semibold text-ink-500 block mb-1.5">
          {label}
        </label>
      )}
      {list.length > 0 && (
        multiple ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items} strategy={rectSortingStrategy}>
              <div className="grid gap-2 mb-2 grid-cols-3 sm:grid-cols-4">
                {list.map((u, i) => (
                  <Thumb key={`${u}-${i}`} id={`${u}-${i}`} url={u} index={i} total={list.length} onRemove={removeAt} testid={testid} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="grid gap-2 mb-2 grid-cols-1">
            <div className="relative group rounded-xl overflow-hidden bg-ivory-100 border border-ink-200/40 aspect-square">
              <img src={resolveImageUrl(list[0])} alt="" className="w-full h-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => removeAt(0)}
                data-testid={`${testid}-remove-0`}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 text-red-600 flex items-center justify-center shadow-soft opacity-0 group-hover:opacity-100 transition"
                aria-label="Rimuovi"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )
      )}
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onClick={() => inputRef.current?.click()}
        data-testid={testid}
        className={`cursor-pointer rounded-xl border-2 border-dashed transition-colors p-5 flex flex-col items-center justify-center text-center gap-2 ${
          dragging
            ? "border-bronze-400 bg-bronze-50/40"
            : "border-ink-200/60 bg-ivory-50 hover:bg-ivory-100"
        }`}
      >
        {busy ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin text-bronze-400" />
            <p className="text-xs text-ink-500">Caricamento e ottimizzazione...</p>
          </>
        ) : (
          <>
            <UploadCloud strokeWidth={1.4} className="w-6 h-6 text-bronze-400" />
            <p className="text-sm text-ink-700 font-medium">
              Trascina qui {multiple ? "le immagini" : "l'immagine"} o tocca per selezionare
            </p>
            <p className="text-[11px] text-ink-300">JPG, PNG, WebP · fino a 12 MB · ottimizzate automaticamente</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid={`${testid}-input`}
        />
      </div>
      {hint && <p className="text-[11px] text-ink-300 mt-1.5">{hint}</p>}
    </div>
  );
}
